import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Newspaper, FileText, Calendar, Scale, Radar,
  Users, Mail, Plus, Trash2, Pencil, ExternalLink, RefreshCw, ArrowLeft, Check, X, Send, Download,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { CATEGORIES, LOGO_URL } from "@/data/constants";
import { US_STATES } from "@/data/states";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = [
  { key: "overview", label: "Overview", Icon: LayoutDashboard },
  { key: "articles", label: "Articles", Icon: Newspaper },
  { key: "guest", label: "Guest Posts", Icon: FileText },
  { key: "events", label: "Events", Icon: Calendar },
  { key: "legislation", label: "Legislation", Icon: Scale },
  { key: "radar", label: "News Radar", Icon: Radar },
  { key: "newsletter", label: "Newsletter", Icon: Send },
  { key: "subscribers", label: "Subscribers", Icon: Users },
  { key: "messages", label: "Messages", Icon: Mail },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline text-[#0A192F] block mb-1">{label}</span>
    {children}
  </label>
);
const input = "w-full px-3 py-2 border border-[#0A192F]/20 outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm";
const btnPrimary = "inline-flex items-center gap-2 bg-[#0A192F] text-white overline px-5 py-2.5 hover:bg-[#0A192F]/90 transition-colors";
const btnGold = "inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] overline px-5 py-2.5 hover:bg-[#e6c14e] transition-colors";

export default function Admin() {
  useSeo({ title: "CMS Dashboard" });
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const refreshStats = useCallback(() => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { refreshStats(); }, [refreshStats]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar */}
      <aside className="w-60 bg-[#0A192F] text-white flex flex-col shrink-0 min-h-screen">
        <div className="p-5 border-b border-white/10">
          <div className="bg-white p-2 rounded-sm inline-block">
            <img src={LOGO_URL} alt="Wolfdog News" className="h-10 w-auto" />
          </div>
          <p className="overline text-[#D4AF37] mt-3">Content Studio</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                tab === key ? "bg-[#D4AF37] text-[#0A192F] font-semibold" : "text-white/70 hover:bg-white/10"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
        <Link to="/" className="p-4 text-xs text-white/50 hover:text-[#D4AF37] flex items-center gap-2 border-t border-white/10">
          <ArrowLeft className="w-4 h-4" /> Back to Site
        </Link>
      </aside>

      <main className="flex-1 p-8 overflow-x-hidden">
        {tab === "overview" && <Overview stats={stats} go={setTab} />}
        {tab === "articles" && <Articles onChange={refreshStats} />}
        {tab === "guest" && <GuestPosts onChange={refreshStats} />}
        {tab === "events" && <Events />}
        {tab === "legislation" && <LegislationAdmin />}
        {tab === "radar" && <RadarAdmin onChange={refreshStats} />}
        {tab === "newsletter" && <NewsletterAdmin onChange={refreshStats} />}
        {tab === "subscribers" && <Subscribers onChange={refreshStats} />}
        {tab === "messages" && <Messages onChange={refreshStats} />}
      </main>
    </div>
  );
}

const Header = ({ title, action }) => (
  <div className="flex items-center justify-between mb-8">
    <h1 className="font-display text-3xl font-black tracking-tight text-[#0A192F]">{title}</h1>
    {action}
  </div>
);

function Overview({ stats, go }) {
  const cards = [
    { label: "Published Articles", value: stats.published, tab: "articles" },
    { label: "Total Articles", value: stats.articles, tab: "articles" },
    { label: "Pending Guest Posts", value: stats.guest_pending, tab: "guest" },
    { label: "Guest Posts", value: stats.guest_total, tab: "guest" },
    { label: "Subscribers", value: stats.subscribers, tab: "subscribers" },
    { label: "Newsletters", value: stats.newsletters, tab: "newsletter" },
    { label: "Messages", value: stats.messages, tab: "messages" },
    { label: "Events", value: stats.events, tab: "events" },
    { label: "Radar Items", value: stats.radar, tab: "radar" },
  ];
  return (
    <div>
      <Header title="Dashboard" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <button key={c.label} onClick={() => go(c.tab)} className="bg-white border border-[#0A192F]/10 p-6 text-left hover:-translate-y-1 hover:border-[#D4AF37] transition-all">
            <p className="text-4xl font-display font-black text-[#0A192F]">{c.value ?? 0}</p>
            <p className="overline text-[#4A5568] mt-2">{c.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

const emptyArticle = { title: "", category: "News", excerpt: "", content: "", image_url: "", author: "Wolfdog News Staff", tags: "", status: "published", featured: false, top_story: false };

function Articles({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);

  const load = () => api.get("/articles", { params: { limit: 200 } }).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyArticle); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...a, tags: (a.tags || []).join(", ") });
    setOpen(true);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setForm({ ...form, [k]: !form[k] });

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
    delete payload.id; delete payload.slug; delete payload.created_at; delete payload.updated_at;
    try {
      if (editing) await api.put(`/articles/${editing.id}`, payload);
      else await api.post("/articles", payload);
      toast.success(editing ? "Article updated" : "Article created");
      setOpen(false); load(); onChange?.();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/articles/${id}`); toast.success("Deleted"); load(); onChange?.();
  };

  return (
    <div>
      <Header title="Articles" action={<button onClick={openNew} data-testid="new-article-button" className={btnPrimary}><Plus className="w-4 h-4" /> New Article</button>} />
      <div className="bg-white border border-[#0A192F]/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A192F]/5 text-left overline text-[#4A5568]">
            <tr><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3">Flags</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {list.map((a) => (
              <tr key={a.id} className="border-t border-[#0A192F]/10">
                <td className="p-3 font-medium text-[#0A192F] max-w-xs truncate">{a.title}</td>
                <td className="p-3 text-[#4A5568]">{a.category}</td>
                <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${a.status === "published" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}`}>{a.status}</span></td>
                <td className="p-3 text-xs text-[#8B6D1F]">{a.top_story ? "Top " : ""}{a.featured ? "Featured" : ""}</td>
                <td className="p-3">
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => openEdit(a)} data-testid={`edit-article-${a.id}`} className="p-2 text-[#0A192F] hover:text-[#8B6D1F]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(a.id)} data-testid={`delete-article-${a.id}`} className="p-2 text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={5} className="p-6 text-center text-[#4A5568]">No articles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Article" : "New Article"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Title"><input value={form.title} onChange={set("title")} data-testid="article-form-title" className={input} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <select value={form.category} onChange={set("category")} className={input + " bg-white"}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={set("status")} className={input + " bg-white"}><option value="published">published</option><option value="draft">draft</option></select>
              </Field>
            </div>
            <Field label="Author"><input value={form.author} onChange={set("author")} className={input} /></Field>
            <Field label="Image URL"><input value={form.image_url} onChange={set("image_url")} className={input} placeholder="https://..." /></Field>
            <Field label="Excerpt"><textarea value={form.excerpt} onChange={set("excerpt")} rows={2} className={input} /></Field>
            <Field label="Content"><textarea value={form.content} onChange={set("content")} rows={7} data-testid="article-form-content" className={input} /></Field>
            <Field label="Tags (comma separated)"><input value={form.tags} onChange={set("tags")} className={input} placeholder="State Laws, Advocacy" /></Field>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.top_story} onChange={toggle("top_story")} data-testid="article-form-topstory" /> Top Story (hero)</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={toggle("featured")} /> Featured</label>
            </div>
            <button onClick={save} data-testid="article-form-save" className={btnGold + " w-full justify-center"}>{editing ? "Update Article" : "Create Article"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GuestPosts({ onChange }) {
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);
  const load = () => api.get("/guest-posts").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/guest-posts/${id}`, { status }); toast.success(`Marked ${status}`); load(); onChange?.();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/guest-posts/${id}`); toast.success("Deleted"); load(); onChange?.(); };
  const badge = { pending: "bg-amber-100 text-amber-900", published: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800" };

  return (
    <div>
      <Header title="Guest Posts" />
      <div className="bg-white border border-[#0A192F]/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A192F]/5 text-left overline text-[#4A5568]">
            <tr><th className="p-3">Title</th><th className="p-3">Author</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {list.map((g) => (
              <tr key={g.id} className="border-t border-[#0A192F]/10">
                <td className="p-3"><button onClick={() => setView(g)} className="font-medium text-[#0A192F] hover:text-[#8B6D1F] text-left">{g.title}</button></td>
                <td className="p-3 text-[#4A5568]">{g.author_name}<br /><span className="text-xs">{g.author_email}</span></td>
                <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${badge[g.status]}`}>{g.status}</span></td>
                <td className="p-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => setStatus(g.id, "published")} data-testid={`approve-guest-${g.id}`} title="Publish" className="p-2 text-green-700 hover:text-green-900"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setStatus(g.id, "rejected")} title="Reject" className="p-2 text-amber-700 hover:text-amber-900"><X className="w-4 h-4" /></button>
                    <button onClick={() => del(g.id)} title="Delete" className="p-2 text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={4} className="p-6 text-center text-[#4A5568]">No submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{view?.title}</DialogTitle></DialogHeader>
          {view && (
            <div>
              <p className="text-sm text-[#4A5568]">By {view.author_name} · {view.category}</p>
              {view.author_bio && <p className="text-sm italic text-[#4A5568] mt-2">{view.author_bio}</p>}
              <div className="mt-4 whitespace-pre-line text-[#1a2b45] leading-relaxed">{view.content}</div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => { setStatus(view.id, "published"); setView(null); }} className={btnGold}>Publish</button>
                <button onClick={() => { setStatus(view.id, "rejected"); setView(null); }} className={btnPrimary}>Reject</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const emptyEvent = { title: "", date: "", location: "", description: "", image_url: "", link: "" };
function Events() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEvent);
  const load = () => api.get("/events").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setEditing(null); setForm(emptyEvent); setOpen(true); };
  const openEdit = (ev) => { setEditing(ev); setForm(ev); setOpen(true); };
  const save = async () => {
    if (!form.title || !form.date) { toast.error("Title and date required"); return; }
    const payload = (({ title, date, location, description, image_url, link }) => ({ title, date, location, description, image_url, link }))(form);
    try {
      if (editing) await api.put(`/events/${editing.id}`, payload); else await api.post("/events", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/events/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <Header title="Events" action={<button onClick={openNew} data-testid="new-event-button" className={btnPrimary}><Plus className="w-4 h-4" /> New Event</button>} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((ev) => (
          <div key={ev.id} className="bg-white border border-[#0A192F]/10 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-[#8B6D1F]">{ev.date}</p>
                <h3 className="font-display font-bold text-[#0A192F]">{ev.title}</h3>
                <p className="text-sm text-[#4A5568]">{ev.location}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(ev)} className="p-2 text-[#0A192F] hover:text-[#8B6D1F]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(ev.id)} className="p-2 text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {!list.length && <p className="text-[#4A5568]">No events yet.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Event" : "New Event"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Title"><input value={form.title} onChange={set("title")} className={input} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date"><input type="date" value={form.date} onChange={set("date")} className={input} /></Field>
              <Field label="Location"><input value={form.location} onChange={set("location")} className={input} /></Field>
            </div>
            <Field label="Image URL"><input value={form.image_url} onChange={set("image_url")} className={input} /></Field>
            <Field label="Link (optional)"><input value={form.link} onChange={set("link")} className={input} /></Field>
            <Field label="Description"><textarea value={form.description} onChange={set("description")} rows={4} className={input} /></Field>
            <button onClick={save} className={btnGold + " w-full justify-center"}>Save Event</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATUSES = ["Legal", "Legal w/ Permit", "Illegal (Grandfathered)", "Illegal", "No Specific Regulation"];
const emptyLeg = { state_code: "AL", state: "Alabama", status: "No Specific Regulation", summary: "", details: "", bills: "" };
function LegislationAdmin() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLeg);
  const load = () => api.get("/legislation").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm(emptyLeg); setOpen(true); };
  const openEdit = (l) => { setForm({ ...l, bills: (l.bills || []).join("\n") }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setState = (e) => {
    const st = US_STATES.find((s) => s.code === e.target.value);
    setForm({ ...form, state_code: st.code, state: st.name });
  };
  const save = async () => {
    const payload = { state: form.state, state_code: form.state_code, status: form.status, summary: form.summary, details: form.details, bills: form.bills ? form.bills.split("\n").map((b) => b.trim()).filter(Boolean) : [] };
    try { await api.post("/legislation", payload); toast.success("Saved"); setOpen(false); load(); } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/legislation/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <Header title="Legislation" action={<button onClick={openNew} data-testid="new-legislation-button" className={btnPrimary}><Plus className="w-4 h-4" /> Add / Update State</button>} />
      <div className="bg-white border border-[#0A192F]/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A192F]/5 text-left overline text-[#4A5568]"><tr><th className="p-3">State</th><th className="p-3">Status</th><th className="p-3">Summary</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.id} className="border-t border-[#0A192F]/10">
                <td className="p-3 font-medium text-[#0A192F]">{l.state}</td>
                <td className="p-3 text-[#4A5568]">{l.status}</td>
                <td className="p-3 text-[#4A5568] max-w-md truncate">{l.summary}</td>
                <td className="p-3"><div className="flex gap-1 justify-end"><button onClick={() => openEdit(l)} className="p-2 text-[#0A192F] hover:text-[#8B6D1F]"><Pencil className="w-4 h-4" /></button><button onClick={() => del(l.id)} className="p-2 text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">State Legislation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="State"><select value={form.state_code} onChange={setState} className={input + " bg-white"}>{US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select></Field>
              <Field label="Status"><select value={form.status} onChange={set("status")} className={input + " bg-white"}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
            </div>
            <Field label="Summary"><textarea value={form.summary} onChange={set("summary")} rows={2} className={input} /></Field>
            <Field label="Details"><textarea value={form.details} onChange={set("details")} rows={5} className={input} /></Field>
            <Field label="Bills / Statutes (one per line)"><textarea value={form.bills} onChange={set("bills")} rows={3} className={input} /></Field>
            <button onClick={save} className={btnGold + " w-full justify-center"}>Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RadarAdmin({ onChange }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/radar", { params: { limit: 50 } }).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const fetchNow = async () => {
    setLoading(true);
    try { const r = await api.post("/radar/fetch"); toast.success(`Fetched ${r.data.fetched}, added ${r.data.inserted} new items`); load(); onChange?.(); }
    catch { toast.error("Fetch failed"); }
    finally { setLoading(false); }
  };
  return (
    <div>
      <Header title="News Radar" action={<button onClick={fetchNow} disabled={loading} data-testid="radar-fetch-button" className={btnGold}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {loading ? "Fetching..." : "Fetch Now"}</button>} />
      <p className="text-sm text-[#4A5568] mb-6">Automatically gathered daily from the web with AI summaries. Runs on a daily schedule; use "Fetch Now" to pull the latest immediately.</p>
      <div className="space-y-3">
        {list.map((r) => (
          <div key={r.id} className="bg-white border border-[#0A192F]/10 p-5 flex items-start justify-between gap-4">
            <div>
              <span className="overline text-[#8B6D1F]">{r.source}</span>
              <h3 className="font-display font-bold text-[#0A192F]">{r.title}</h3>
              <p className="text-sm text-[#4A5568] mt-1">{r.summary}</p>
            </div>
            <a href={r.url} target="_blank" rel="noopener noreferrer" className="p-2 text-[#0A192F] hover:text-[#8B6D1F] shrink-0"><ExternalLink className="w-5 h-5" /></a>
          </div>
        ))}
        {!list.length && <p className="text-[#4A5568]">No radar items yet. Click "Fetch Now" to gather the latest wolfdog news.</p>}
      </div>
    </div>
  );
}

function Subscribers({ onChange }) {
  const [list, setList] = useState([]);
  const load = () => api.get("/newsletter").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/newsletter/${id}`); load(); onChange?.(); };
  return (
    <div>
      <Header title="Newsletter Subscribers" action={
        <a href={`${API}/newsletter/export`} data-testid="download-subscribers-csv" className={btnGold}>
          <Download className="w-4 h-4" /> Download CSV
        </a>
      } />
      <p className="text-sm text-[#4A5568] mb-6">
        Export your subscriber list as a CSV and import it into Brevo (or another bulk-email provider) to send your newsletter.
      </p>
      <div className="bg-white border border-[#0A192F]/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A192F]/5 text-left overline text-[#4A5568]"><tr><th className="p-3">Email</th><th className="p-3">Subscribed</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-t border-[#0A192F]/10">
                <td className="p-3 text-[#0A192F]">{s.email}</td>
                <td className="p-3 text-[#4A5568]">{new Date(s.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right"><button onClick={() => del(s.id)} className="p-2 text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={3} className="p-6 text-center text-[#4A5568]">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Messages({ onChange }) {
  const [list, setList] = useState([]);
  const load = () => api.get("/contact").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/contact/${id}`); load(); onChange?.(); };
  return (
    <div>
      <Header title="Contact Messages" />
      <div className="space-y-3">
        {list.map((m) => (
          <div key={m.id} className="bg-white border border-[#0A192F]/10 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-display font-bold text-[#0A192F]">{m.subject || "(No subject)"}</p>
                <p className="text-sm text-[#4A5568]">{m.name} · {m.email}</p>
              </div>
              <button onClick={() => del(m.id)} className="p-2 text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="mt-3 text-[#1a2b45] leading-relaxed whitespace-pre-line">{m.message}</p>
          </div>
        ))}
        {!list.length && <p className="text-[#4A5568]">No messages yet.</p>}
      </div>
    </div>
  );
}

const emptyNewsletter = { title: "", author: "Wolfdog News", excerpt: "", content: "", image_url: "", status: "published" };
function NewsletterAdmin({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyNewsletter);

  const load = () => api.get("/newsletters").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyNewsletter); setOpen(true); };
  const openEdit = (n) => { setEditing(n); setForm({ ...n }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    if (!form.content) { toast.error("Content is required"); return; }
    const payload = { title: form.title, author: form.author, excerpt: form.excerpt, content: form.content, image_url: form.image_url, status: form.status };
    try {
      if (editing) await api.put(`/newsletters/${editing.id}`, payload);
      else await api.post("/newsletters", payload);
      toast.success(editing ? "Newsletter updated" : "Newsletter published");
      setOpen(false); load(); onChange?.();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this newsletter?")) return; await api.delete(`/newsletters/${id}`); toast.success("Deleted"); load(); onChange?.(); };

  return (
    <div>
      <Header title="Newsletter" action={
        <div className="flex items-center gap-2">
          <a href={`${API}/newsletter/export`} data-testid="download-subscribers-csv-nl" className={btnPrimary}><Download className="w-4 h-4" /> Subscribers CSV</a>
          <button onClick={openNew} data-testid="new-newsletter-button" className={btnGold}><Plus className="w-4 h-4" /> New Newsletter</button>
        </div>
      } />
      <div className="bg-[#0A192F]/5 border border-[#0A192F]/10 p-4 mb-6 text-sm text-[#1a2b45] leading-relaxed">
        <strong>How this works:</strong> Publish a newsletter here and it becomes a permanent, searchable page on WolfdogNews.com
        (with its own URL, date, author, and social-sharing preview). To email it to your list, click <em>Subscribers CSV</em> and
        import the file into <strong>Brevo</strong> to send the bulk email from there.
      </div>
      <div className="bg-white border border-[#0A192F]/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A192F]/5 text-left overline text-[#4A5568]">
            <tr><th className="p-3">Title</th><th className="p-3">Author</th><th className="p-3">Status</th><th className="p-3">Published</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {list.map((n) => (
              <tr key={n.id} className="border-t border-[#0A192F]/10">
                <td className="p-3 font-medium text-[#0A192F] max-w-xs truncate">{n.title}</td>
                <td className="p-3 text-[#4A5568]">{n.author}</td>
                <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${n.status === "published" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600"}`}>{n.status}</span></td>
                <td className="p-3 text-[#4A5568]">{n.published_at ? new Date(n.published_at).toLocaleDateString() : ""}</td>
                <td className="p-3">
                  <div className="flex items-center gap-2 justify-end">
                    <a href={`/newsletter/${n.slug}`} target="_blank" rel="noopener noreferrer" title="View on site" className="p-2 text-[#0A192F] hover:text-[#8B6D1F]"><ExternalLink className="w-4 h-4" /></a>
                    <button onClick={() => openEdit(n)} data-testid={`edit-newsletter-${n.id}`} className="p-2 text-[#0A192F] hover:text-[#8B6D1F]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(n.id)} data-testid={`delete-newsletter-${n.id}`} className="p-2 text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={5} className="p-6 text-center text-[#4A5568]">No newsletters yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Newsletter" : "New Newsletter"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Title"><input value={form.title} onChange={set("title")} data-testid="newsletter-form-title" className={input} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Author"><input value={form.author} onChange={set("author")} className={input} /></Field>
              <Field label="Status">
                <select value={form.status} onChange={set("status")} className={input + " bg-white"}><option value="published">published</option><option value="draft">draft</option></select>
              </Field>
            </div>
            <Field label="Cover Image URL"><input value={form.image_url} onChange={set("image_url")} className={input} placeholder="https://..." /></Field>
            <Field label="Excerpt (optional — used in previews & social sharing)"><textarea value={form.excerpt} onChange={set("excerpt")} rows={2} className={input} /></Field>
            <Field label="Content"><textarea value={form.content} onChange={set("content")} rows={10} data-testid="newsletter-form-content" className={input} placeholder="Write your issue. Use @@H@@ for a heading and @@IMG@@https://url@@caption@@ for an inline image." /></Field>
            <button onClick={save} data-testid="newsletter-form-save" className={btnGold + " w-full justify-center"}>{editing ? "Update Newsletter" : "Publish Newsletter"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
